import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Home.css";
import Footer from "@shared/Footer/Footer";
import NavMenu from "../../shared/NavBar/NavBar.jsx";
import HeroSection from "./HeroSection/HeroSecton";
import ContextHeroSection from "./Sections/ContextHeroSection/ContextHeroSection";
import SeasonalWelcome from "./SeasonalWelcome";
import { faqRecords } from "@lib/faq/faqRecords";
import { FAQ_LAST_UPDATED, getLandingFaqPreview } from "@lib/faq/faqUtils";

const WatchSection = lazy(() => import("./WatchSection/WatchSection.jsx"));
const LegalIntelligence = lazy(() => import("./LegalIntelligence/LegalIntelligence.jsx"));
const ProductiveAssistant = lazy(() => import("./ProductiveAssistant/ProductiveAssistant.jsx"));
const SecurityCompliance = lazy(() => import("./SecurityCompliance/SecurityCompliance.jsx"));
const CompetitiveSection = lazy(() => import("./CompetitiveSection/CompetitiveSection.jsx"));
const FAQSection = lazy(() => import("@shared/FAQ/FAQSection"));

const EMAIL_VIENNA_HREF = "mailto:vienna@nougat.law?subject=Question%20about%20Nougat";

function LandingCta({ kind, id }) {
  return (
    <div className="home-page-cta">
      {kind === "email" ? (
        <a id={id} className="home-page-cta__link button button--primary-new" href={EMAIL_VIENNA_HREF}>
          Email Vienna
        </a>
      ) : (
        <Link className="home-page-cta__link button button--primary-new" to="/signup">
          Start saving up to 15% of billables
        </Link>
      )}
    </div>
  );
}

const SEASONAL_WELCOME_SESSION_KEY = "juristai-home-seasonal-welcome-shown";

function hasShownSeasonalWelcomeThisSession() {
  try {
    return window.sessionStorage.getItem(SEASONAL_WELCOME_SESSION_KEY) === "true";
  } catch {
    return false;
  }
}

function DeferredSection({
  children,
  eagerOnMobile = false,
  forceRender = false,
  id,
  minHeight = 0,
  rootMargin = "320px",
}) {
  const [shouldRender, setShouldRender] = useState(forceRender);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (forceRender) {
      setShouldRender(true);
      return undefined;
    }

    if (eagerOnMobile && window.matchMedia("(max-width: 768px)").matches) {
      setShouldRender(true);
      return undefined;
    }

    const node = sectionRef.current;
    if (!node || shouldRender) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [eagerOnMobile, forceRender, rootMargin, shouldRender]);

  return (
    <section
      ref={sectionRef}
      className="deferred-section"
      id={id}
      style={{ minHeight: shouldRender ? 0 : minHeight }}
    >
      {shouldRender ? <Suspense fallback={null}>{children}</Suspense> : null}
    </section>
  );
}

const Home = () => {
  const [watchDemoRequest, setWatchDemoRequest] = useState(0);
  // Show the seasonal animation once per session, after six headline loops.
  const [seasonalRun, setSeasonalRun] = useState(0);
  const seasonalLoopCount = useRef(0);
  const seasonalShownThisSession = useRef(hasShownSeasonalWelcomeThisSession());
  const handleSeasonalAnimationStart = useCallback(() => {
    if (seasonalRun || seasonalShownThisSession.current) return;

    seasonalLoopCount.current += 1;
    if (seasonalLoopCount.current >= 6) {
      seasonalShownThisSession.current = true;
      try {
        window.sessionStorage.setItem(SEASONAL_WELCOME_SESSION_KEY, "true");
      } catch {
        // Keep the animation limited to one run while this Home instance is mounted.
      }
      setSeasonalRun(1);
    }
  }, [seasonalRun]);
  const landingFaqs = getLandingFaqPreview(faqRecords, 8);

  useEffect(() => {
    if (!watchDemoRequest) {
      return undefined;
    }

    let frameId;
    let cancelled = false;
    const scrollToVideo = () => {
      if (cancelled) {
        return;
      }

      const video = document.querySelector("#watch-demo .watch-section__video-wrapper");
      if (video) {
        video.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      frameId = window.requestAnimationFrame(scrollToVideo);
    };

    frameId = window.requestAnimationFrame(scrollToVideo);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
    };
  }, [watchDemoRequest]);

  const handleWatchDemo = () => setWatchDemoRequest((request) => request + 1);

  return (
    <div className="home-page-container">
      {seasonalRun ? <SeasonalWelcome key={seasonalRun} /> : null}
      <NavMenu />
      <main id="main-content">

        <div className="content ">
          <HeroSection
            onWatchDemo={handleWatchDemo}
            onSeasonalAnimationStart={handleSeasonalAnimationStart}
          />
          <ContextHeroSection />
          <LandingCta kind="email" id="email-vienna-after-context" />
          <DeferredSection id="features" eagerOnMobile forceRender={watchDemoRequest > 0} minHeight={820} rootMargin="0px 0px 120px 0px">
            <LegalIntelligence />
          </DeferredSection>
          <LandingCta kind="signup" />
          <DeferredSection id="how-it-works" forceRender={watchDemoRequest > 0} minHeight={680}>
            <ProductiveAssistant />
          </DeferredSection>
          <LandingCta kind="email" />
          <DeferredSection id="security" forceRender={watchDemoRequest > 0} minHeight={900}>
            <SecurityCompliance />
          </DeferredSection>
          <LandingCta kind="signup" />
          <DeferredSection forceRender={watchDemoRequest > 0} minHeight={860}>
            <CompetitiveSection />
          </DeferredSection>
          <LandingCta kind="email" />
          <DeferredSection forceRender={watchDemoRequest > 0} minHeight={384}>
            <FAQSection
              routeKey="home"
              title="Answers before you get started"
              items={landingFaqs}
              lastUpdated={FAQ_LAST_UPDATED}
              viewAllHref="/faq"
              showTitle={false}
              showLead={false}
            />
          </DeferredSection>
          <DeferredSection
            id="watch-demo"
            forceRender={watchDemoRequest > 0}
            minHeight={560}
            rootMargin="0px 0px 120px 0px"
          >
            <WatchSection />
          </DeferredSection>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Home;
